export interface AvatarItem {
  id: string;
  name: string;
  imageUrl: string;
}

export interface Subscription {
  _id: string;
  wechatUserId: string;
  fromUsers: Array<string>;
  fromGroups: Array<string>;
  keyword: string;
  at: boolean;
  replyType: number;
  content: string;
  messageTypes: Array<string>;
}

export const ReplyTypesDict = ["Mirror", "Direct", "Indirect", "Notify"];

export const Device = {
  MAC: "mac",
  IPAD: "ipad",
} as const;
export type DeviceType = (typeof Device)[keyof typeof Device];

export const DEFAULT_DEVICE: DeviceType = Device.IPAD;

export const deviceLabel = (device: DeviceType): string =>
  device === Device.MAC ? "Mac" : "iPad";

// ---------- API contracts ----------

export type ApiResponse<T = unknown> =
  | { successful: true; result: T; message?: string }
  | { successful: false; message: string; result?: undefined };

export interface Platform {
  _id: string;
  username: string;
  baseUrl: string;
  callbackUrl: string;
  authorization?: string;
  // Local-only fields decorated by the UI:
  order?: number;
  device?: DeviceType;
  proxyArea?: string;
}

export interface WechatAccount {
  wcId: string;
  wId: string;
  platformId: string;
  username: string;
  wAccount: string;
  nickName: string;
  smallHeadImgUrl: string;
  headUrl: string;
  mobilePhone: string;
  online: boolean;
  disabled?: boolean;
  deviceType?: DeviceType;
  area?: string;
  // Local-only:
  order?: number;
}

export interface WechatContact {
  v1: string;
  userName: string;
  nickName: string;
  smallHead: string;
  bigHead?: string;
  remark?: string;
  // Local-only:
  order?: number;
}

export interface WechatChatroomMember {
  wcId?: string;
  userName: string;
  nickName: string;
  smallHeadImgUrl: string;
  // Local-only:
  order?: number;
}

export interface WechatAccountDetail extends WechatAccount {
  // Optional: absent for offline / freshly-logged-in accounts whose address
  // book hasn't been fetched yet (server returns the raw cached user object).
  friends?: Array<WechatContact>;
  chatrooms?: Array<WechatContact>;
}

export interface QrCodeResult {
  platformId: string;
  wId: string;
  wcId: string;
  qrCodeUrl: string;
}

export type WechatLoginResult = WechatAccount | string;

export interface Task {
  _id: string;
  type?: number;
  wechatUserId: string;
  target: string;
  cron: string;
  content: string;
  at: string;
  keyword?: string;
  // Local-only:
  order?: number;
}

export interface SubscriptionRecord extends Subscription {
  // Local-only:
  order?: number;
}

// ---------- chat history ----------

export interface MessageContact {
  // Personal wcId for private chats, or a `*@chatroom` id for groups.
  peerId: string;
  isGroup: boolean;
  name: string;
  avatar: string;
  lastContent: string;
  lastMessageType: string;
  lastTimestamp: number;
  count: number;
}

// Mirrors the server-side MessageData (loose — only the fields the UI renders).
export interface ChatMessageData {
  fromUser: string;
  toUser?: string;
  fromGroup?: string;
  remoteUser?: string;
  self?: boolean;
  content: string;
  msgId?: number;
  newMsgId?: number;
  timestamp?: number;
  createTime?: number;
  nickName?: string;
  url?: string;
  file?: string;
  thumbUrl?: string;
  title?: string;
  description?: string;
  voiceLength?: number;
  length?: number;
  [key: string]: unknown;
}

export interface ChatMessage {
  _id: string;
  wcId: string;
  messageType: string;
  account: string;
  data: ChatMessageData;
}

export interface MessageHistoryPage {
  total: number;
  items: Array<ChatMessage>;
}
